import { Clock, CheckCircle, XCircle } from 'lucide-react';

const DAYS = [
  { key: 'mon', label: 'Monday', short: 'Mon' },
  { key: 'tue', label: 'Tuesday', short: 'Tue' },
  { key: 'wed', label: 'Wednesday', short: 'Wed' },
  { key: 'thu', label: 'Thursday', short: 'Thu' },
  { key: 'fri', label: 'Friday', short: 'Fri' },
  { key: 'sat', label: 'Saturday', short: 'Sat' },
  { key: 'sun', label: 'Sunday', short: 'Sun' }
];

function formatTime(time24) {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

function isOpenNow(hours) {
  if (!hours) return null;
  
  const now = new Date();
  const dayIndex = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const dayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const todayKey = dayKeys[dayIndex];
  
  const todayHours = hours[todayKey];
  if (!todayHours || todayHours.closed) return false;
  
  const currentTime = now.getHours() * 60 + now.getMinutes();
  const [openHour, openMin] = todayHours.open.split(':').map(Number);
  const [closeHour, closeMin] = todayHours.close.split(':').map(Number);
  
  const openTime = openHour * 60 + openMin;
  const closeTime = closeHour * 60 + closeMin;
  
  return currentTime >= openTime && currentTime < closeTime;
}

function getTodayKey() {
  const dayIndex = new Date().getDay();
  const dayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  return dayKeys[dayIndex];
}

export default function BusinessHoursDisplay({ hours, compact = false }) {
  if (!hours) return null;
  
  const openStatus = isOpenNow(hours);
  const todayKey = getTodayKey();
  
  if (compact) {
    const todayHours = hours[todayKey];
    return (
      <div className="flex items-center gap-2">
        {openStatus === true ? (
          <>
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span className="text-green-400 font-medium">Open</span>
            {todayHours && !todayHours.closed && (
              <span className="text-zinc-500 text-sm">
                · Closes {formatTime(todayHours.close)}
              </span>
            )}
          </>
        ) : openStatus === false ? (
          <>
            <XCircle className="w-4 h-4 text-red-400" />
            <span className="text-red-400 font-medium">Closed</span>
          </>
        ) : (
          <>
            <Clock className="w-4 h-4 text-zinc-500" />
            <span className="text-zinc-500">Hours not available</span>
          </>
        )}
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-zinc-400" />
          <span className="font-medium text-white">Business Hours</span>
        </div>
        
        {openStatus === true ? (
          <span className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-500/10 text-green-400 text-sm">
            <CheckCircle className="w-3.5 h-3.5" />
            Open Now
          </span>
        ) : openStatus === false ? (
          <span className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-500/10 text-red-400 text-sm">
            <XCircle className="w-3.5 h-3.5" />
            Closed
          </span>
        ) : null}
      </div>
      
      <div className="space-y-2">
        {DAYS.map(({ key, label }) => {
          const dayHours = hours[key];
          const isToday = key === todayKey;
          
          return (
            <div
              key={key}
              className={`flex items-center justify-between py-2 px-3 rounded-lg ${
                isToday ? 'bg-zinc-800/50' : ''
              }`}
            >
              <span className={`text-sm ${isToday ? 'text-white font-medium' : 'text-zinc-400'}`}>
                {label}
                {isToday && <span className="text-xs text-zinc-500 ml-2">(Today)</span>}
              </span>
              
              {dayHours?.closed ? (
                <span className="text-zinc-600 text-sm">Closed</span>
              ) : dayHours?.open && dayHours?.close ? (
                <span className={`text-sm ${isToday ? 'text-white' : 'text-zinc-300'}`}>
                  {formatTime(dayHours.open)} - {formatTime(dayHours.close)}
                </span>
              ) : (
                <span className="text-zinc-600 text-sm">—</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
